import { computed, ComputedRef, ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import { ChallengeMaterial } from "@/types/v2/task/challenge/material.types"
import {
  ChallengeConfiguration,
  StandardChallengeConfiguration,
  BathChallengeConfiguration,
  isStandardChallengeConfiguration,
  isBathChallengeConfiguration,
  isSimplifiedBath,
  isStandardBath
} from "@/types/v2/task/challenge/configuration.types"
import { ChallengeDilution } from "@/types/v2/task/challenge/dilution.types"
import { ConfiguredChallengeMaterial } from "@/types/v2/task/challenge/material.types"

import {
  Tank,
  TankGroup,
  GroupTag,
  VaccineMethod,
} from "@/types/v2";

import { isChallenge } from "@/stores/v2/tasks/utils";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";

// Bath process status for tracking
export interface BathProcessStatus {
  configKey: string;
  status: 'not_started' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  expectedDuration: number; // in minutes
  actualDuration?: number; // in minutes
  isEarly?: boolean;
  isLate?: boolean;
}

export function useChallengeConfigurations() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());

  // ===================================================================
  // Types
  // ===================================================================
  type ChallengeMaterialUpdateKey = keyof ConfiguredChallengeMaterial;
  type StandardConfigUpdateKey = keyof StandardChallengeConfiguration;
  type BathConfigUpdateKey = keyof BathChallengeConfiguration;

  // Only include keys that are safe to update for each type
  type StandardUpdateableKeys = 'sourceTank' | 'destinationTank' | 'sourceGroup' | 'destinationGroup' | 'targetAmount' | 'challengeDuration' | 'instructions';
  type BathUpdateableKeys = 'tank' | 'sourceTank' | 'destinationTank' | 'sourceGroup' | 'destinationGroup' | 'targetAmount' | 'challengeDuration' | 'requiresWeighing' | 'instructions';

  type UpdateKey = ChallengeMaterialUpdateKey | StandardUpdateableKeys | BathUpdateableKeys;

  // ===================================================================
  // Constants
  // ===================================================================
  const CONTEXT_CHALLENGE_CONFIGURATION = "ChallengeConfiguration";

  // ===================================================================
  // Composables
  // ===================================================================
  const { getHistory, recordChange } = useTaskHistory();

  // ===================================================================
  // State
  // ===================================================================
  const configurations: ComputedRef<ChallengeConfiguration[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.configurations || [];
  });

  // Separate standard and bath configurations
  const standardConfigurations: ComputedRef<StandardChallengeConfiguration[]> = computed(() => {
    return configurations.value.filter(isStandardChallengeConfiguration);
  });

  const bathConfigurations: ComputedRef<BathChallengeConfiguration[]> = computed(() => {
    return configurations.value.filter(isBathChallengeConfiguration);
  });

  // Task type detection
  const isBathTask: ComputedRef<boolean> = computed(() => {
    return bathConfigurations.value.length > 0;
  });

  const isStandardTask: ComputedRef<boolean> = computed(() => {
    return standardConfigurations.value.length > 0;
  });

  const isMixedTask: ComputedRef<boolean> = computed(() => {
    return isStandardTask.value && isBathTask.value;
  });

  // Bath process status tracking (reactive state)
  const bathProcessStatuses = ref<Record<string, BathProcessStatus>>({});

  // Initialize status for configurations
    const initializeBathStatuses = () => {
      bathConfigurations.value.forEach(config => {
        if (!bathProcessStatuses.value[config.key]) {
          let status: 'not_started' | 'in_progress' | 'completed' = 'not_started';
          let startedAt: string | undefined;
          let completedAt: string | undefined;

          // Check actual bathProcess state
          if (config.bathProcess?.completedAt) {
            status = 'completed';
            completedAt = config.bathProcess.completedAt;
            startedAt = config.bathProcess.startedAt;
          } else if (config.bathProcess?.startedAt) {
            status = 'in_progress';
            startedAt = config.bathProcess.startedAt;
          }

          bathProcessStatuses.value[config.key] = {
            configKey: config.key,
            status,
            startedAt,
            completedAt,
            expectedDuration: config.challengeDuration * 60, // Convert hours to minutes
          };
        }
      });
    };

  // Auto-initialize when configurations change
  watch(bathConfigurations, initializeBathStatuses, { immediate: true });

  // ===================================================================
  // Actions - Standard Configuration (existing logic)
  // ===================================================================
  const createEmptyStandardConfiguration = (): StandardChallengeConfiguration => {
    if (!activeTask.value || !isChallenge(activeTask.value)) {
      throw new Error("Not a challenge task");
    }

    const config: StandardChallengeConfiguration = {
      key: uuidv4(),
      challengeMaterials: [],
      sourceTank: { key: uuidv4(), name: "" } as Tank,
      sourceGroup: { key: uuidv4(), name: "" } as TankGroup,
      targetAmount: 0,
      destinationTank: { key: uuidv4(), name: "" } as Tank,
      destinationGroup: { key: uuidv4(), name: "" } as TankGroup,
    };

    activeTask.value.details.configurations.push(config);
    return config;
  };

  // ===================================================================
  // Actions - Bath Configuration (new logic)
  // ===================================================================
  const createEmptyBathConfiguration = (isSimplified = true): BathChallengeConfiguration => {
    if (!activeTask.value || !isChallenge(activeTask.value)) {
      throw new Error("Not a challenge task");
    }

    const config: BathChallengeConfiguration = {
      key: uuidv4(),
      challengeMaterials: [],
      targetAmount: 0,
      challengeDuration: 24, // Default 24 hours
      requiresWeighing: false,
      isSimplifiedBath: isSimplified,
      isStandardBath: !isSimplified,
    };

    if (isSimplified) {
      config.tank = { key: uuidv4(), name: "" } as Tank;
    } else {
      config.sourceTank = { key: uuidv4(), name: "" } as Tank;
      config.destinationTank = { key: uuidv4(), name: "" } as Tank;
      config.sourceGroup = { key: uuidv4(), name: "" } as TankGroup;
      config.destinationGroup = { key: uuidv4(), name: "" } as TankGroup;
    }

    activeTask.value.details.configurations.push(config);
    return config;
  };

// Start bath process
  const startBathProcess = async (configIndex: number): Promise<boolean> => {
    const config = configurations.value[configIndex];
    if (!config || !isBathChallengeConfiguration(config)) {
      return false;
    }

    if (!activeTask.value || !isChallenge(activeTask.value)) {
      return false;
    }

    const startTime = new Date().toISOString();

    // Update the activeTask.value directly
    const taskConfig = activeTask.value.details.configurations[configIndex] as BathChallengeConfiguration;

    // Initialize bathProcess object if it doesn't exist
    if (!taskConfig.bathProcess) {
      taskConfig.bathProcess = {};
    }

    // Set the startedAt timestamp
    taskConfig.bathProcess.startedAt = startTime;

    // Record the change for history tracking
    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].bathProcess.startedAt`,
      fieldName: 'Bath Process Started',
      oldValue: null,
      newValue: startTime,
      context: 'BathChallengeConfiguration',
      contextType: 'execution',
      workflowStage: 'bath_process',
      requireJustification: false,
      justification: ''
    });

    if (success) {
      // Update the local status tracking
      bathProcessStatuses.value[config.key] = {
        configKey: config.key,
        status: 'in_progress',
        startedAt: startTime,
        expectedDuration: config.challengeDuration * 60, // Convert hours to minutes
      };

      console.log(`Started bath process for config ${config.key} - Duration: ${config.challengeDuration} hours`);
      return true;
    } else {
      // If recordChange failed, revert the direct update
      if (taskConfig.bathProcess) {
        delete taskConfig.bathProcess.startedAt;
      }
      return false;
    }
  };

  const finishBathProcess = async (configIndex: number): Promise<boolean> => {
    const config = configurations.value[configIndex];

    if (!config || !isBathChallengeConfiguration(config)) {
      return false;
    }

    if (!activeTask.value || !isChallenge(activeTask.value)) {
      return false;
    }

    const currentStatus = bathProcessStatuses.value[config.key];
    if (!currentStatus || !currentStatus.startedAt) {
      console.error('Cannot finish process that was not started');
      return false;
    }

    // Calculate timing
    const startTime = new Date(currentStatus.startedAt);
    const expectedDurationMs = config.challengeDuration * 60 * 60 * 1000;
    const actualDurationMs = Date.now() - startTime.getTime();
    const isEarly = actualDurationMs < expectedDurationMs * 0.95;
    const isLate = actualDurationMs > expectedDurationMs * 1.05;
    const requiresJustification = isEarly || isLate;

    const finishTimeISO = new Date().toISOString();

    // Update the activeTask.value directly
    const taskConfig = activeTask.value.details.configurations[configIndex] as BathChallengeConfiguration;

    if (!taskConfig.bathProcess) {
      taskConfig.bathProcess = {};
    }

    taskConfig.bathProcess.completedAt = finishTimeISO;

    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].bathProcess.completedAt`,
      fieldName: 'Bath Process Completed',
      oldValue: null,
      newValue: finishTimeISO,
      context: 'BathChallengeConfiguration',
      contextType: 'execution',
      workflowStage: 'bath_process',
      requireJustification: requiresJustification,
      justification: ''
    });

    if (success) {
      // Update the local status tracking
      bathProcessStatuses.value[config.key] = {
        ...currentStatus,
        status: 'completed',
        completedAt: finishTimeISO,
        actualDuration: Math.round(actualDurationMs / (60 * 1000)),
        isEarly,
        isLate,
      };

      return true;
    } else {
      if (taskConfig.bathProcess) {
        delete taskConfig.bathProcess.completedAt;
      }
      return false;
    }
  };

  // ===================================================================
  // Actions - Universal (works for both types)
  // ===================================================================
  const updateChallengeMaterialProperty = (
    challengeMaterial: ConfiguredChallengeMaterial,
    key: ChallengeMaterialUpdateKey,
    item: unknown
  ): void => {
    switch (key) {
      case "material":
        challengeMaterial[key] = {
          ...challengeMaterial[key],
          name: (item as ChallengeMaterial).name,
        } as ChallengeMaterial;
        break;
      case "method":
        challengeMaterial[key] = {
          ...challengeMaterial[key],
          name: (item as VaccineMethod).name,
        } as VaccineMethod;
        break;
      case "dilution":
        challengeMaterial[key] = item as ChallengeDilution;
        break;
      case "dose":
        challengeMaterial.dose = item as number;
        break;
    }
  };

  const updateConfigurationProperty = (
    configuration: ChallengeConfiguration,
    key: UpdateKey,
    item: unknown
  ): void => {
    if (isStandardChallengeConfiguration(configuration)) {
      // Handle standard configuration updates
      const standardKey = key as StandardUpdateableKeys;
      switch (standardKey) {
        case "sourceTank":
        case "destinationTank":
          if (standardKey in configuration) {
            (configuration as any)[standardKey] = item as Tank;
          }
          break;
        case "sourceGroup":
        case "destinationGroup":
          if (standardKey in configuration) {
            (configuration as any)[standardKey] = item as TankGroup;
          }
          break;
        case "targetAmount":
          configuration[standardKey] = item as number;
          break;
        case "challengeDuration":
          if (standardKey in configuration) {
            (configuration as any)[standardKey] = item as number;
          }
          break;
        case "instructions":
          if (standardKey in configuration) {
            (configuration as any)[standardKey] = item as string;
          }
          break;
      }
    } else if (isBathChallengeConfiguration(configuration)) {
      // Handle bath configuration updates
      const bathKey = key as BathUpdateableKeys;
      switch (bathKey) {
        case "tank":
          if (bathKey in configuration) {
            (configuration as any)[bathKey] = item as Tank;
          }
          break;
        case "sourceTank":
        case "destinationTank":
          if (bathKey in configuration) {
            (configuration as any)[bathKey] = item as Tank;
          }
          break;
        case "sourceGroup":
        case "destinationGroup":
          if (bathKey in configuration) {
            (configuration as any)[bathKey] = item as TankGroup;
          }
          break;
        case "targetAmount":
          configuration[bathKey] = item as number;
          break;
        case "challengeDuration":
          configuration[bathKey] = item as number;
          break;
        case "requiresWeighing":
          configuration[bathKey] = item as boolean;
          break;
        case "instructions":
          if (bathKey in configuration) {
            (configuration as any)[bathKey] = item as string;
          }
          break;
      }
    }
  };

  const updateConfiguration = async (
    configIndex: number,
    nestedIndex: number,
    key: UpdateKey,
    item: unknown
  ): Promise<void> => {
    if (!activeTask.value || !isChallenge(activeTask.value) || !item)
      return Promise.resolve();

    const configuration = configurations.value[configIndex];
    if (!configuration) return Promise.resolve();

    if (nestedIndex !== -1 && configuration.challengeMaterials) {
      const challengeMaterial = configuration.challengeMaterials[nestedIndex];
      if (challengeMaterial) {
        updateChallengeMaterialProperty(
          challengeMaterial,
          key as ChallengeMaterialUpdateKey,
          item
        );
      }
    } else {
      updateConfigurationProperty(configuration, key, item);
    }
  };

  // Append new tag to Destination Group (only for standard configs)
  const appendTagToDestinationGroup = async (
    configIndex: number,
    newTag: GroupTag
  ): Promise<boolean> => {
    if (!activeTask.value || !isChallenge(activeTask.value)) {
      return false;
    }

    const configuration = configurations.value[configIndex];
    if (!configuration || !isStandardChallengeConfiguration(configuration)) {
      return false; // Bath configs don't use destination groups in the same way
    }

    if (newTag.title === "Start Tag") {
      return false;
    }

    const updatedDestGroup = configuration.destinationGroup
      ? { ...configuration.destinationGroup }
      : { key: uuidv4(), name: "" } as TankGroup;

    if (!updatedDestGroup.tags) {
      updatedDestGroup.tags = [];
    } else {
      updatedDestGroup.tags = [...updatedDestGroup.tags];
    }

    const tagExists = updatedDestGroup.tags.some(tag => tag.title === newTag.title);

    if (!tagExists) {
      updatedDestGroup.tags.push(newTag);
      await updateConfiguration(configIndex, -1, 'destinationGroup', updatedDestGroup);
      return true;
    }

    return false;
  };

  // ===================================================================
  // Getters
  // ===================================================================
  const getConfigurationTitle = (configuration: ChallengeConfiguration): string => {
    if (isStandardChallengeConfiguration(configuration)) {
      return configuration.destinationTank?.label ||
        configuration.destinationTank?.name ||
        "New Configuration";
    }

    // Must be bath configuration at this point
    const bathConfig = configuration as BathChallengeConfiguration;

    if (bathConfig.isSimplifiedBath && bathConfig.tank) {
      return `${bathConfig.tank.name || bathConfig.tank.label || "Unknown Tank"}`;
    } else if (bathConfig.isStandardBath && bathConfig.sourceTank && bathConfig.destinationTank) {
      console.log(bathConfig.sourceTank.name);
      return `${bathConfig.sourceTank.name || "Unknown"} → ${bathConfig.destinationTank.name || "Unknown"}`;
    }

    return "Bath Configuration";
  };

  const getConfigurationTags = (configuration: ChallengeConfiguration): GroupTag[] => {
    if (!isStandardChallengeConfiguration(configuration)) {
      // Handle bath configurations
      if (isBathChallengeConfiguration(configuration)) {
        const bathConfig = configuration as BathChallengeConfiguration;

        if (bathConfig.isStandardBath && bathConfig.destinationGroup) {
          // For standard bath, only show destination group tags (which include source + new tags)
          const destinationTags = bathConfig.destinationGroup?.tags || [];

          return destinationTags.filter(tag => tag.title !== "Start Tag");
        }
      }

      return [];
    }

    // Original logic for standard challenge configurations - also only destination tags
    const destinationTags = configuration.destinationGroup?.tags || [];

    return destinationTags.filter(tag => tag.title !== "Start Tag");
  };

  const getConfigurationTotalWeight = (configKey: string): number => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return 0;

    const weightConfiguration = activeTask.value.details.weightConfiguration;
    if (!weightConfiguration?.progressData) return 0;

    return weightConfiguration.progressData
      .filter((record) => record.configurationKey === configKey)
      .reduce((sum, record) => sum + record.weight, 0);
  };

  const getChallengeConfigurationsHistory = () => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];

    const fullHistory = getHistory();
    return fullHistory.filter(item => item.context === CONTEXT_CHALLENGE_CONFIGURATION);
  };

  const getChallengeRecordHistory = (configKey: string, entryIndex: number) => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];

    const configIndex = activeTask.value.details.configurations.findIndex(
      config => config.key === configKey
    );

    if (configIndex === -1) return [];

    const allHistory = activeTask.value.history?.entries || [];

    return allHistory.filter(entry => {
      return entry.fieldPath.includes(`configurations[${configIndex}].progressData[${entryIndex}]`) &&
        (entry.fieldName === 'Challenged Fish' ||
          entry.fieldName === 'Weight' ||
          entry.fieldName === 'Challenge Administration');
    }).map(entry => {
      return {
        field: entry.fieldName,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
        reason: entry.justification,
        userName: entry.userName,
        timestamp: entry.timestamp
      };
    });
  };

  // Get bath process status
  const getBathProcessStatus = (configKey: string): BathProcessStatus | null => {
    return bathProcessStatuses.value[configKey] || null;
  };

  return {
    // ===================================================================
    // State
    // ===================================================================
    configurations,
    standardConfigurations,
    bathConfigurations,
    bathProcessStatuses,

    // ===================================================================
    // Computed
    // ===================================================================
    isBathTask,
    isStandardTask,
    isMixedTask,

    // ===================================================================
    // Getters
    // ===================================================================
    getConfigurationTitle,
    getConfigurationTags,
    getConfigurationTotalWeight,
    getChallengeRecordHistory,
    getChallengeConfigurationsHistory,
    getBathProcessStatus,

    // ===================================================================
    // Actions - Standard
    // ===================================================================
    createEmptyConfiguration: createEmptyStandardConfiguration, // Keep backward compatibility
    createEmptyStandardConfiguration,
    updateConfiguration,
    appendTagToDestinationGroup,

    // ===================================================================
    // Actions - Bath
    // ===================================================================
    createEmptyBathConfiguration,
    startBathProcess,
    finishBathProcess,
  };
}